import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../FirebaseConfig";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster, toast } from 'react-hot-toast';
import { Canvas } from "@react-three/fiber";
import { OrbitControls, MeshDistortMaterial, Sphere } from "@react-three/drei";

// 3D Animated Background Component
const AnimatedBackground = () => (
  <Canvas className="!fixed top-0 left-0 w-full h-full -z-10 opacity-30">
    <OrbitControls enableZoom={false} autoRotate />
    <ambientLight intensity={1.5} />
    <directionalLight position={[3, 2, 1]} />
    <Sphere args={[1.5, 64, 32]}>
      <MeshDistortMaterial
        color="#22C55E"
        attach="material"
        distort={0.4}
        speed={1.5}
        roughness={0.5}
        transparent
        opacity={0.6}
      />
    </Sphere>
  </Canvas>
);

// A modern, styled Popup component
const Popup = ({ message, type = "success" }) => {
  const bgColor = type === "success" ? "bg-green-500" : "bg-red-500";
  return (
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 50, opacity: 0 }}
      transition={{ duration: 0.3 }}
      className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-xl shadow-2xl text-white font-medium ${bgColor} z-50`}
    >
      {message}
    </motion.div>
  );
};

const ListingForm = () => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    profilePic: "",
    gender: "",
    role: "",
  });
  const [imageUploading, setImageUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({
    visible: false,
    message: "",
    type: "error",
  });
  const navigate = useNavigate();

  // Fetch user + profile
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(db, "profiles", currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserProfile(data);
          setFormData((prev) => ({
            ...prev,
            name: data.name || "",
            phoneNumber: data.phoneNumber || "",
            profilePic: data.profilePic || "",
            gender: data.gender || "",
            role: data.role || "",
          }));
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const isVerified = userProfile?.verification_status === "approved";

  const showPopup = (message, type = "error") => {
    setPopup({ visible: true, message, type });
    setTimeout(() => {
      setPopup((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "unsigned_ibuyunn");
    setImageUploading(true);

    try {
      const res = await fetch(
        "https://api.cloudinary.com/v1_1/dm8uirujp/upload",
        { method: "POST", body: data }
      );
      const result = await res.json();
      setFormData({ ...formData, profilePic: result.secure_url });
      showPopup("Profile picture uploaded successfully! 🎉", "success");
    } catch (error) {
      console.error("Image upload failed:", error);
      showPopup("Image upload failed. Please try again.");
    } finally {
      setImageUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) {
      showPopup("You must be logged in to proceed.");
      return;
    }

    const { name, phoneNumber, role, gender, profilePic } = formData;
    if (!name || !phoneNumber || !role || !gender || !profilePic) {
      showPopup("Please fill in all fields before saving.");
      return;
    }

    setLoading(true);
    try {
      const userDocRef = doc(db, "profiles", user.uid);
      await setDoc(
        userDocRef,
        {
          ...formData,
          userId: user.uid,
          userEmail: user.email,
          createdAt: new Date(),
          isProfileComplete: true,
        },
        { merge: true }
      );

      showPopup("Profile saved successfully! Redirecting...", "success");
      setTimeout(() => {
        navigate("/AddProperty");
      }, 2000);
    } catch (error) {
      console.error("Error saving profile:", error.message);
      showPopup("Error saving profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const buttonVariants = {
    hover: { scale: 1.05, boxShadow: "0px 8px 20px rgba(0, 255, 0, 0.3)" },
    tap: { scale: 0.95 },
  };
  
  if (userProfile && !isVerified) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-transparent text-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-lg w-full p-8 rounded-2xl shadow-2xl text-white bg-white/10 backdrop-blur-xl border border-white/20"
        >
          <h1 className="text-3xl font-bold mb-4">Access Denied</h1>
          <p className="text-lg text-gray-300 mb-6">You must be a verified user to list a property. Please complete the verification process first.</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate("/verify")}
            className="w-full px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors duration-200 shadow-lg"
          >
            Go to Verification
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-transparent">
      <AnimatedBackground />
      <AnimatePresence>
        {popup.visible && <Popup message={popup.message} type={popup.type} />}
      </AnimatePresence>
      <Toaster />

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative w-full max-w-lg p-8 rounded-2xl shadow-2xl border border-white/20 bg-white/10 backdrop-blur-lg z-10"
      >
        <motion.h1 
          className="text-4xl font-extrabold text-white text-center mb-2 drop-shadow-lg" 
          variants={itemVariants}
        >
          Complete Your Profile ✨
        </motion.h1>
        <motion.p 
          className="text-gray-300 text-center mb-8 font-light"
          variants={itemVariants}
        >
          Fill this form to start listing properties.
        </motion.p>

        <motion.form variants={containerVariants} className="space-y-6">
          <motion.input
            variants={itemVariants}
            type="text"
            name="name"
            placeholder="Full Name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-5 py-3 rounded-xl bg-white/5 text-white placeholder-gray-400 border border-white/20 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors duration-300"
          />
          <motion.input
            variants={itemVariants}
            type="number"
            name="phoneNumber"
            placeholder="Phone Number"
            value={formData.phoneNumber}
            onChange={handleChange}
            className="w-full px-5 py-3 rounded-xl bg-white/5 text-white placeholder-gray-400 border border-white/20 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors duration-300"
          />

          <motion.div variants={itemVariants}>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-5 py-3 rounded-xl bg-white/5 text-gray-300 border border-white/20 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors duration-300"
            >
              <option value="" className="bg-gray-800 text-gray-400">
                Select Role
              </option>
              <option value="Student" className="bg-gray-800 text-white">
                Student
              </option>
              <option value="Agent" className="bg-gray-800 text-white">
                Agent
              </option>
              <option value="LandLord" className="bg-gray-800 text-white">
                LandLord
              </option>
              <option value="Care Taker" className="bg-gray-800 text-white">
                Care Taker
              </option>
            </select>
          </motion.div>

          <motion.div variants={itemVariants}>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className="w-full px-5 py-3 rounded-xl bg-white/5 text-gray-300 border border-white/20 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors duration-300"
            >
              <option value="" className="bg-gray-800 text-gray-400">
                Select Gender
              </option>
              <option value="male" className="bg-gray-800 text-white">
                Male
              </option>
              <option value="female" className="bg-gray-800 text-white">
                Female
              </option>
            </select>
          </motion.div>

          <motion.label
            variants={itemVariants}
            htmlFor="File"
            className="flex items-center justify-center p-4 rounded-xl cursor-pointer text-gray-300 border-dashed border-2 border-white/30 hover:bg-white/5 transition-colors duration-200"
          >
            <span className="font-medium text-white mr-3">
              {imageUploading ? "Uploading..." : "Upload Profile Picture"}
            </span>
            {imageUploading ? (
              <div className="animate-spin w-5 h-5 border-2 border-green-400 border-t-transparent rounded-full"></div>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            )}
            <input
              type="file"
              id="File"
              className="sr-only"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={imageUploading}
            />
          </motion.label>
        </motion.form>

        <AnimatePresence>
          {formData.profilePic && (
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-8 flex justify-center"
            >
              <img
                src={formData.profilePic}
                alt="Profile"
                className="w-28 h-28 object-cover rounded-full border-4 border-green-400 shadow-xl"
              />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.button
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          onClick={handleSaveProfile}
          disabled={loading}
          className={`w-full py-4 px-6 mt-10 rounded-xl text-white font-bold transition-all duration-300 transform ${
            loading
              ? "bg-gray-600 cursor-not-allowed"
              : "bg-green-500 hover:bg-green-600"
          }`}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
              Saving Profile...
            </div>
          ) : (
            "Save Profile"
          )}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default ListingForm;